// Emory University: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Emory's Common Data Set 2025-2026 (Office of the Provost PDF, May 2026):
//    C1 applicants/admits/enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027, C21 early decision counts. The CDS reports
//    the university as one; it does not split Emory College from Oxford
//    College.
//  - apply.emory.edu: Student Profile (Emory College and Oxford College
//    Class of 2030 admission profiles, data as of June 2026), Plans and
//    Deadlines, Early Decision, Standardized Exam Policies. One Common App
//    reaches Emory College, Oxford College, or both; the deadlines are the
//    same for both colleges.
//  - Emory Office of Financial Aid, Cost of Attendance Worksheet 2026-2027
//    (the worksheet the aid site lists for Emory College, Oxford College and
//    Goizueta Business School; last updated 7/1/2026).
//  - College Scorecard API, school id 139658 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Emory Common Data Set 2025-2026',
        url: 'https://provost.emory.edu/planning-administration/_includes/documents/sections/institutional-data/emory-common-date-set-2025-2026.pdf',
        read: '2026-09-30',
    },
    classProfile: {
        label: 'Emory Admissions, Student Profile',
        url: 'https://apply.emory.edu/why-emory/student-profile',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'Emory Admissions, Plans and Deadlines',
        url: 'https://apply.emory.edu/apply/plans-deadlines',
        read: '2026-09-30',
    },
    earlyDecision: {
        label: 'Emory Admissions, Early Decision',
        url: 'https://apply.emory.edu/apply/plans-deadlines/early-decision',
        read: '2026-09-30',
    },
    tests: {
        label: 'Emory Admissions, Standardized Exam Policies',
        url: 'https://apply.emory.edu/apply/requirements/exams',
        read: '2026-09-30',
    },
    costs: {
        label: 'Emory Office of Financial Aid, Cost of Attendance Worksheet 2026-2027',
        url: 'https://studentaid.emory.edu/_includes/documents/sections/undergraduate/apply/cost-of-attendance-worksheet.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?139658-Emory-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'emory-university',
    kbId: 'emory_university',
    name: 'Emory University',
    shortName: 'Emory',
    location: 'Atlanta, GA',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Emory colors); decoration only.
    branding: {
        primary: '#002F6C',
        secondary: '#F2A900',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/emory_university_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: emory university acceptance rate.
    faq: {
        answer: { text: 'Emory University admitted 10.3% of first-year applicants for fall 2025, 3,869 of the 37,561 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Emory acceptance rate?', a: 'For fall 2025 entry, Emory admitted 3,869 of 37,561 first-year applicants, an acceptance rate of 10.3%, and 1,443 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What are the rates for Emory College and Oxford College?', a: 'Emory\'s own profiles for the Class of 2030, who started in fall 2026, give 9% for Emory College (3,852 of 42,873) and 10% for Oxford College (2,773 of 27,113), as Emory rounds them. One application can go to either college or both.', source: 'classProfile', where: 'Class of 2030 Admission Profiles' },
            { q: 'What is the Emory early decision acceptance rate?', a: 'For fall 2025, Emory admitted 1,049 of 4,728 Early Decision I and II applicants, 22.2%. The Common Data Set reports the two rounds together.', source: 'cds', where: 'section C21' },
            { q: 'What are Emory\'s deadlines for fall 2027?', a: 'Early Decision I is November 1, with decisions by December 15. Early Decision II and Regular Decision are both January 1, with decisions by February 15 and April 1. Both Early Decision plans are binding.', source: 'deadlines' },
            { q: 'Does Emory require the SAT or ACT?', a: 'No. Emory is test-optional for students who would start in fall 2027, but not test-blind: scores you send are reviewed, and Emory superscores both the SAT and the ACT.', source: 'tests' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 37561,
        admitted: 3869,
        enrolled: 1443,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4380, admitted: 447 },
            { group: 'Out-of-state', applied: 23974, admitted: 2846 },
            { group: 'International', applied: 9207, admitted: 576 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2026',
        published: true,
        note: 'Emory publishes an admission profile for each of its two first-year colleges. These are for the Class of 2030, who started in fall 2026, with data as of June 2026: a year newer than the Common Data Set counts above. You can apply to Emory College, Oxford College, or both with one application. The rates are as Emory rounds them.',
        head: ['College', 'Applied', 'Admitted', 'Rate'],
        rows: [
            { name: 'Emory College', applied: 42873, admitted: 3852, rate: 9 },
            { name: 'Oxford College', applied: 27113, admitted: 2773, rate: 10 },
        ],
        source: 'classProfile',
        where: 'Class of 2030 Admission Profiles',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025.',
        rows: [
            { plan: 'Early Decision I and II', applied: 4728, admitted: 1049 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'By April 1', binding: false },
        ],
        note: 'The same deadlines apply to Emory College and Oxford College. The Emory University Scholar Programs deadline is November 15, with decisions by March 1.',
        source: 'deadlines',
        binding: 'Both Early Decision plans are binding: if Emory makes the education affordable, you must pay your deposit and enroll. The Early Decision agreement must be signed by you, a parent or guardian, and your school counselor. You can apply early decision to Emory College, Oxford College, or both, and if you are admitted to both you choose which to attend. Deposits are due January 15 for Early Decision I and March 1 for Early Decision II.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Emory is test-optional for students who would start in fall 2027: SAT and ACT scores are not required. It is not test-blind: if you send scores, they are reviewed as a sign of your academic preparation. Emory superscores both the SAT and the ACT.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required but considered if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 70300 },
            { item: 'Fees', amount: 1148 },
            { item: 'Housing', amount: 13222 },
            { item: 'Food', amount: 9184 },
            { item: 'Travel', amount: 1100 },
            { item: 'Personal', amount: 1620 },
            { item: 'Books', amount: 1286 },
            { item: 'Direct loan fees', amount: 88 },
        ],
        total: 97948,
        note: 'The total leaves out Emory\'s student health insurance, which you are enrolled in unless you have a comparable plan.',
        source: 'costs',
        netPrice: {
            average: 16754,
            byIncome: [
                { income: '$0 to $30,000', amount: 10593 },
                { income: '$30,001 to $48,000', amount: 8932 },
                { income: '$48,001 to $75,000', amount: 9448 },
                { income: '$75,001 to $110,000', amount: 13089 },
                { income: 'Over $110,000', amount: 49057 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Emory\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
